import React, { useState } from 'react'
import { useUserLogs } from '../../hooks/useUserLogs'
import { estoqueService } from '../../services/estoque'
import type { EstoqueFornecedor } from '../../types/estoque'
import EstoqueFornecedorFormFields from './EstoqueFornecedorFormFields'
import EstoqueModal, { EstoqueModalActions } from './EstoqueModal'
import {
  emptyFornecedorForm,
  validateFornecedorForm,
  type EstoqueFornecedorFormValues,
} from './estoqueUi'

type Props = {
  open: boolean
  onClose: () => void
  onCreated: (fornecedor: EstoqueFornecedor) => void
}

const EstoqueFornecedorCreateModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const { logCreate } = useUserLogs()
  const [form, setForm] = useState<EstoqueFornecedorFormValues>(() => emptyFornecedorForm())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!open) return null

  const handleClose = () => {
    if (saving) return
    setForm(emptyFornecedorForm())
    setError(null)
    onClose()
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const validationError = validateFornecedorForm(form)
    if (validationError) {
      setError(validationError)
      return
    }
    setSaving(true)
    setError(null)
    try {
      const created = await estoqueService.createFornecedor(form)
      void logCreate(
        'supplier',
        created.id,
        `Criou fornecedor de estoque: ${created.name}`,
        {
          name: created.name,
          address: created.address,
          city: created.city,
          state: created.state,
          contact_name: created.contact_name,
          is_active: created.is_active,
        }
      )
      setForm(emptyFornecedorForm())
      onCreated(created)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar fornecedor.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <EstoqueModal
      title="Novo fornecedor"
      onClose={handleClose}
      busy={saving}
      onSubmit={handleSave}
      zClass="z-[60]"
      footer={<EstoqueModalActions onCancel={handleClose} saving={saving} />}
    >
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}
      <EstoqueFornecedorFormFields form={form} onChange={setForm} />
    </EstoqueModal>
  )
}

export default EstoqueFornecedorCreateModal
